import { Card, CardContent } from "../components/ui/card";
import { CheckCircle, Award, Users, Zap } from "lucide-react";
import heroImage from "../assets/hero-coldroom.jpg";
import founderImage from "../assets/Mr_Zulu.jpg"; // <-- Add your founder image here

const About = () => {
  return (
    <div className="min-h-screen">
      {/* HERO SECTION */}
      <section
        className="relative text-white py-20 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.55)), url(${heroImage})`,
        }}
      >
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">About 3A Coldrooms</h1>
          <p className="text-xl max-w-3xl mx-auto">
            Your trusted Level 1 BEE partner in professional refrigeration, coldroom,
            and air conditioning solutions across South Africa.
          </p>
        </div>
        <div className="absolute inset-0 bg-black/40"></div>
      </section>

      {/* MISSION & VISION */}
      <section className="py-16 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <Card>
              <CardContent className="pt-6">
                <h2 className="text-2xl font-bold mb-3">Our Mission</h2>
                <p className="text-muted-foreground leading-relaxed">
                  To deliver reliable, energy-efficient refrigeration and air conditioning solutions that
                  protect our clients’ products, comfort, and bottom line through honest advice, 
                  quality workmanship, and responsive support.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <h2 className="text-2xl font-bold mb-3">Our Vision</h2>
                <p className="text-muted-foreground leading-relaxed">
                  To be South Africa’s most trusted provider of coldroom, mobile refrigeration, and HVAC systems by
                  setting the benchmark for performance, sustainability, and customer care in every project we deliver.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* LEADERSHIP / FOUNDER SECTION */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold mb-6 text-center">Our Leadership</h2>
          <Card>
            <CardContent className="pt-6 flex flex-col md:flex-row items-center gap-6">
              <img 
                src={founderImage} 
                alt="Mr. Sibusiso “Bhabha” Zulu" 
                className="w-40 h-40 rounded-full object-cover shadow-lg"
              />
              <div className="space-y-4 text-muted-foreground text-lg">
                <p>
                  <strong>Mr. Sibusiso “Bhabha” Zulu</strong> is the founder and owner of 3A Coldrooms & Airconditioning. 
                  A Durban University of Technology alumnus, he established the company in 2007, combining creativity, 
                  technical skill, and community-driven purpose to build one of South Africa’s leading coldroom 
                  and HVAC providers.
                </p>
                <p>
                  His journey from the arts to industrial manufacturing reflects his resilience and innovation. 
                  Under his leadership, 3A Coldrooms has supported major national events, including the 2010 FIFA World Cup, 
                  and continues to grow through a focus on quality, sustainability, and client satisfaction.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* OUR STORY */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6">Our Story</h2>
            <div className="space-y-4 text-lg text-muted-foreground">
              <p>
                3A Coldrooms & Airconditioning is a proudly black-owned, Level 1 BEE company based in Durban, 
                providing expert manufacturing, installation, and maintenance services across the cold chain and air conditioning sectors. 
                Established in 2007, we have built a strong reputation for reliability, technical excellence, and superior service quality.
              </p>
              <p>
                With more than 20 years in operation, our expertise spans coldroom and fridge manufacturing, 
                mortuary equipment, mobile trailers (for hire and sale), and full-service HVAC solutions for 
                commercial and industrial clients.
              </p>
              <p>
                Our journey began with the vision of founder <strong>Mr. Sibusiso “Bhabha” Zulu</strong>, 
                a Durban University of Technology alumnus whose entrepreneurial spirit transformed 
                a personal challenge into a thriving enterprise. Inspired by a family experience where 
                food spoiled due to lack of refrigeration, he founded 3A Coldrooms — turning necessity 
                into innovation.
              </p>
              <p>
                Under Mr. Zulu’s leadership, the company has achieved several milestones, including providing 
                mobile cold rooms for the 2010 FIFA World Cup. Despite challenges such as the COVID-19 pandemic, 
                3A Coldrooms has remained resilient, expanding into maintenance and service contracts that ensure 
                long-term sustainability and value for clients nationwide.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-16 bg-muted">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">Why Choose Us</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardContent className="pt-6 text-center">
                <Award className="w-12 h-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Certified Professionals</h3>
                <p className="text-muted-foreground">
                  Fully qualified and certified technicians with years of hands-on industry experience.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6 text-center">
                <CheckCircle className="w-12 h-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Quality Guarantee</h3>
                <p className="text-muted-foreground">
                  All work is backed by warranties and strict quality assurance processes.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6 text-center">
                <Users className="w-12 h-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Trusted by Leading Clients</h3>
                <p className="text-muted-foreground">
                  We proudly serve clients such as Eskom, Department of Health, eThekwini Metro, 
                  Nelson Mandela Metro, and AVBOB.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6 text-center">
                <Zap className="w-12 h-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Innovation & Efficiency</h3>
                <p className="text-muted-foreground">
                  Modern systems designed for performance, sustainability, and energy efficiency.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* OUR COMMITMENT */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6 text-center">Our Commitment</h2>
            <Card>
              <CardContent className="pt-6">
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <strong className="text-lg">Quality Workmanship:</strong>
                      <p className="text-muted-foreground">Every installation meets the highest standards of quality and safety.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <strong className="text-lg">Timely Service:</strong>
                      <p className="text-muted-foreground">We deliver on schedule, respecting client timelines and operational needs.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <strong className="text-lg">Transparent Pricing:</strong>
                      <p className="text-muted-foreground">Clear, upfront quotations with no hidden costs or surprises.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <strong className="text-lg">Ongoing Support:</strong>
                      <p className="text-muted-foreground">Comprehensive maintenance and emergency callout services.</p>
                    </div>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
